import { useState, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Checkbox } from '@/components/ui/checkbox';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { FormFieldMessage } from './FormFieldMessage';
import { Card } from '@/components/ui/card';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useNetworkMembers } from '@/hooks/useNetworkMembers';
import { 
  Target, 
  Users, 
  Search, 
  X, 
  Loader2, 
  CheckCircle2,
  Rocket
} from 'lucide-react';

type FormStep = 'objective' | 'team' | 'review';

const STEP_ORDER: FormStep[] = ['objective', 'team', 'review'];

interface SprintCreationInlineFormProps {
  userId: string;
  onComplete: (sprintId: string) => void;
  onCancel: () => void;
}

export const SprintCreationInlineForm = ({ userId, onComplete, onCancel }: SprintCreationInlineFormProps) => {
  const [currentStep, setCurrentStep] = useState<FormStep>('objective');
  const [objective, setObjective] = useState('');
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [manualEmails, setManualEmails] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [isCreating, setIsCreating] = useState(false);

  const { data: networkMembers = [], isLoading: loadingNetwork } = useNetworkMembers(userId);

  const filteredMembers = useMemo(() => {
    if (!searchTerm) return networkMembers;
    const term = searchTerm.toLowerCase();
    return networkMembers.filter(
      member =>
        member.display_name?.toLowerCase().includes(term) ||
        member.username?.toLowerCase().includes(term) ||
        member.organization?.toLowerCase().includes(term)
    );
  }, [networkMembers, searchTerm]);

  const getCurrentStepIndex = () => STEP_ORDER.indexOf(currentStep);
  const progress = ((getCurrentStepIndex() + 1) / STEP_ORDER.length) * 100;

  const handleToggleUser = (memberId: string) => {
    setSelectedUserIds(prev =>
      prev.includes(memberId)
        ? prev.filter(id => id !== memberId)
        : [...prev, memberId]
    );
  };

  const handleSelectAll = () => {
    setSelectedUserIds(filteredMembers.map(m => m.id));
  };

  const handleDeselectAll = () => {
    setSelectedUserIds([]);
  };

  const parseEmails = (emailString: string): string[] => {
    return emailString
      .split(/[,\s]+/)
      .map(e => e.trim().toLowerCase())
      .filter(e => e && e.includes('@'));
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const selectedCount = selectedUserIds.length + parseEmails(manualEmails).length;

  const canProceed = () => {
    switch (currentStep) {
      case 'objective':
        return objective.trim().length >= 20;
      case 'team':
        return true; // Team is optional
      case 'review':
        return true;
      default:
        return false;
    }
  };

  const handleLaunchSprint = async () => {
    if (objective.trim().length < 20) {
      toast.error('Objective must be at least 20 characters');
      return;
    }

    setIsCreating(true);

    try {
      // Create the sprint
      const { data: sprint, error: sprintError } = await supabase
        .from('prompt_architecture_sprints')
        .insert({
          creator_id: userId,
          objective: objective.trim(),
          status: 'active',
        })
        .select()
        .single();

      if (sprintError) throw sprintError;

      // Add creator as participant
      await supabase.from('sprint_participants').insert({
        sprint_id: sprint.id,
        user_id: userId,
        role: 'creator'
      });

      // Send invitations to network members
      const emails = parseEmails(manualEmails);
      
      for (const memberId of selectedUserIds) {
        const member = networkMembers.find(m => m.id === memberId);
        if (member) {
          // Create sprint invitation
          await supabase.from('sprint_invitations').insert({
            sprint_id: sprint.id,
            inviter_id: userId,
            invitee_user_id: memberId,
            invitee_email: '', // Will be filled from profile
            status: 'pending'
          });
        }
      }

      // Send email invitations to external users
      for (const email of emails) {
        await supabase.from('sprint_invitations').insert({
          sprint_id: sprint.id,
          inviter_id: userId,
          invitee_email: email,
          status: 'pending'
        });

        // Optionally trigger email via edge function
        try {
          await supabase.functions.invoke('send-sprint-invitation', {
            body: {
              sprint_id: sprint.id,
              invitee_email: email,
              inviter_id: userId
            }
          });
        } catch (emailError) {
          console.error('Failed to send email invitation:', emailError);
        }
      }

      toast.success('Sprint launched successfully!');
      onComplete(sprint.id);
    } catch (error) {
      console.error('Error creating sprint:', error);
      toast.error('Failed to create sprint');
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <div className="space-y-3 animate-in fade-in slide-in-from-bottom-4 duration-500">
      {/* Progress Bar */}
      <FormFieldMessage variant="assistant">
        <div className="flex items-center gap-3 mb-2">
          <div className="text-sm font-medium">
            Step {getCurrentStepIndex() + 1} of {STEP_ORDER.length}
          </div>
          <Progress value={progress} className="flex-1 h-2" />
        </div>
        <div className="mt-2 inline-flex items-center gap-2 px-3 py-1 bg-primary/10 text-primary rounded-full text-sm font-medium">
          <Rocket className="h-4 w-4" />
          Start Team Sprint
        </div>
      </FormFieldMessage>

      {/* Step: Objective */}
      {currentStep === 'objective' && (
        <FormFieldMessage variant="assistant">
          <Label className="text-base font-semibold mb-3 block flex items-center gap-2">
            <Target className="h-5 w-5 text-primary" />
            What's the objective for this sprint?
          </Label>
          <Textarea
            value={objective}
            onChange={(e) => setObjective(e.target.value.slice(0, 1000))}
            placeholder="Describe what you want to achieve with this prompt architecture sprint..."
            className="min-h-[120px]"
          />
          <div className="flex justify-between items-center mt-2">
            <div className="text-xs text-muted-foreground">
              {objective.length}/1000 characters
            </div>
            {objective.trim() && objective.length < 20 && (
              <p className="text-xs text-destructive">
                At least 20 characters required
              </p>
            )}
          </div>
        </FormFieldMessage>
      )}

      {/* Step: Team */}
      {currentStep === 'team' && (
        <FormFieldMessage variant="assistant">
          <Label className="text-base font-semibold mb-3 block flex items-center gap-2">
            <Users className="h-5 w-5 text-primary" />
            Who would you like to invite?
            {selectedCount > 0 && (
              <span className="text-sm font-normal text-muted-foreground">
                ({selectedCount} selected)
              </span>
            )}
          </Label>
          
          <Tabs defaultValue="network" className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="network">Your Network</TabsTrigger>
              <TabsTrigger value="email">Invite by Email</TabsTrigger>
            </TabsList>

            <TabsContent value="network" className="mt-4 space-y-3">
              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search your network..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9"
                />
              </div>

              {/* Select/Deselect All */}
              {filteredMembers.length > 0 && (
                <div className="flex gap-2 text-sm">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={handleSelectAll}
                    className="h-7 px-2"
                  >
                    Select all
                  </Button>
                  {selectedUserIds.length > 0 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={handleDeselectAll}
                      className="h-7 px-2"
                    >
                      Deselect all
                    </Button>
                  )}
                </div>
              )}

              {/* Network Members List */}
              <ScrollArea className="h-[180px] border rounded-md">
                {loadingNetwork ? (
                  <div className="flex items-center justify-center h-full">
                    <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                  </div>
                ) : filteredMembers.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-full text-center p-4">
                    <Users className="h-8 w-8 text-muted-foreground mb-2" />
                    <p className="text-sm text-muted-foreground">
                      {searchTerm
                        ? 'No members match your search'
                        : 'No network members yet. Use the Email tab to invite collaborators.'}
                    </p>
                  </div>
                ) : (
                  <div className="p-2 space-y-1">
                    {filteredMembers.map((member) => (
                      <label
                        key={member.id}
                        className="flex items-center gap-3 p-2 rounded-md hover:bg-muted/50 cursor-pointer"
                      >
                        <Checkbox
                          checked={selectedUserIds.includes(member.id)}
                          onCheckedChange={() => handleToggleUser(member.id)}
                        />
                        <Avatar className="h-8 w-8">
                          <AvatarImage src={member.avatar_url} />
                          <AvatarFallback className="text-xs">
                            {getInitials(member.display_name || '?')}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">
                            {member.display_name}
                          </p>
                          {(member.title || member.organization) && (
                            <p className="text-xs text-muted-foreground truncate">
                              {[member.title, member.organization].filter(Boolean).join(' • ')}
                            </p>
                          )}
                        </div>
                      </label>
                    ))}
                  </div>
                )}
              </ScrollArea>

              {/* Selected Members Preview */}
              {selectedUserIds.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {selectedUserIds.map(id => {
                    const member = networkMembers.find(m => m.id === id);
                    if (!member) return null;
                    return (
                      <div
                        key={id}
                        className="flex items-center gap-1 bg-primary/10 text-primary px-2 py-1 rounded-full text-xs"
                      >
                        <span>{member.display_name}</span>
                        <button
                          type="button"
                          onClick={() => handleToggleUser(id)}
                          className="hover:bg-primary/20 rounded-full p-0.5"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </TabsContent>

            <TabsContent value="email" className="mt-4 space-y-3">
              <p className="text-sm text-muted-foreground">
                Know someone who should join but isn't on iSPAI yet? Invite them by email.
              </p>
              <Input
                type="text"
                value={manualEmails}
                onChange={(e) => setManualEmails(e.target.value)}
                placeholder="email1@example.com, email2@example.com"
              />
              <p className="text-xs text-muted-foreground">
                Separate multiple emails with commas.
              </p>

              {/* Email Preview */}
              {parseEmails(manualEmails).length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {parseEmails(manualEmails).map(email => (
                    <div
                      key={email}
                      className="bg-muted text-muted-foreground px-2 py-1 rounded-full text-xs"
                    >
                      {email}
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>

          <p className="text-xs text-muted-foreground mt-3">
            Optional — you can also invite team members after the sprint is created.
          </p>
        </FormFieldMessage>
      )}

      {/* Step: Review */}
      {currentStep === 'review' && (
        <FormFieldMessage variant="assistant">
          <Label className="text-base font-semibold mb-3 block flex items-center gap-2">
            <CheckCircle2 className="h-5 w-5 text-green-600" />
            Ready to Launch?
          </Label>
          <Card className="p-4 space-y-3 bg-muted/30">
            <div>
              <p className="text-xs text-muted-foreground">Sprint Objective</p>
              <p className="text-sm">{objective}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Team Invitations</p>
              {selectedCount === 0 ? (
                <p className="text-sm text-muted-foreground">No invitations (you can add team members later)</p>
              ) : (
                <div className="flex flex-wrap gap-1 mt-1">
                  {selectedUserIds.map(id => {
                    const member = networkMembers.find(m => m.id === id);
                    return member ? (
                      <span key={id} className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded">
                        {member.display_name}
                      </span>
                    ) : null;
                  })}
                  {parseEmails(manualEmails).map(email => (
                    <span key={email} className="text-xs bg-muted text-muted-foreground px-2 py-0.5 rounded">
                      {email}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </Card>
        </FormFieldMessage>
      )}

      {/* Navigation Buttons */}
      <FormFieldMessage variant="user">
        <div className="flex justify-between gap-2">
          <Button
            variant="outline"
            onClick={() => {
              if (currentStep === 'objective') {
                onCancel();
              } else {
                const idx = getCurrentStepIndex();
                if (idx > 0) {
                  setCurrentStep(STEP_ORDER[idx - 1]);
                }
              }
            }}
            size="sm"
            disabled={isCreating}
          >
            {currentStep === 'objective' ? 'Cancel' : 'Back'}
          </Button>

          {currentStep === 'review' ? (
            <Button
              onClick={handleLaunchSprint}
              disabled={isCreating || objective.trim().length < 20}
              size="sm"
              className="bg-green-700 hover:bg-green-800"
            >
              {isCreating ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Launching...
                </>
              ) : (
                <>
                  <Rocket className="mr-2 h-4 w-4" />
                  Launch Sprint
                </>
              )}
            </Button>
          ) : (
            <Button
              onClick={() => {
                const idx = getCurrentStepIndex();
                if (idx < STEP_ORDER.length - 1) {
                  setCurrentStep(STEP_ORDER[idx + 1]);
                }
              }}
              disabled={!canProceed()}
              size="sm"
            >
              Continue
            </Button>
          )}
        </div>
      </FormFieldMessage>
    </div>
  );
};
